import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import NotificationIcon from "@components/Notifications/NotificationIcon";
import UserMenu from "@components/UserMenu/UserMenu";
import { useUserMenuData } from "@components/UserMenu/useUserMenuData";
import { useTravelSearchShortcut } from "@hooks/useTravelSearchShortcut";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import layoutStyles from "@components/Layout/Layout.module.css";
import sidebarStyles from "@components/Sidebar/Sidebar.module.css"; // Reuse sidebar styles for logo if possible, or define in layout
import Sidebar from "@components/Sidebar/Sidebar";
import SettingsLayout from "@components/Settings/SettingsLayout";
import MenuSvg from "@assets/svgs/MenuSvg";
import { useCompanyLogo } from "@hooks/useCompanyLogo";
import { useGetSubscriptionStatusQuery } from "@store/api";
import {
  HiOutlineSparkles,
  HiOutlineChatBubbleLeftRight,
} from "react-icons/hi2";
import {
  getRolesForPath,
  getDefaultRouteForRoles,
} from "@constants/routeConfig";

/**
 * AuthLayout Component
 *
 * Layout for authenticated routes: sidebar and main content.
 * Used when the user has a valid token; auth is decided in App, not here.
 *
 * @param {React.ReactNode} children - Child components to render in main content area
 */
const AuthLayout = ({ children }) => {
  const { t } = useTranslation();
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const wasMobileSidebarOpen = useRef(false);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const handleMobileSidebarChange = useCallback((open) => {
    setMobileSidebarOpen(open);
  }, []);
  useEffect(() => {
    if (wasMobileSidebarOpen.current && !isMobileSidebarOpen && isMobile) {
      menuButtonRef.current?.focus();
    }
    wasMobileSidebarOpen.current = isMobileSidebarOpen;
  }, [isMobileSidebarOpen, isMobile]);
  const company = useSelector((state) => state.user?.company);
  const companyId = useSelector((state) => state.user?.companyId);
  const userRoles = useSelector((state) => state.user?.roles) || [];
  const canManageBilling =
    userRoles.includes("SUPER_ADMIN") || userRoles.includes("PLATFORM_ADMIN");
  const { data: liveSub } = useGetSubscriptionStatusQuery(
    { company_id: companyId },
    { skip: !companyId || !canManageBilling, pollingInterval: 5 * 60 * 1000 },
  );
  const subscriptionStatus = liveSub?.status ?? company?.subscription?.status;
  const showSubscriptionBanner =
    subscriptionStatus === "past_due" || subscriptionStatus === "suspended";

  const { logoUrl, logoError, logoLoaded, handleLoad, handleError } =
    useCompanyLogo(company);

  const hasAgentPermission = getRolesForPath("/agent").some((role) =>
    userRoles.includes(role),
  );
  const hasTravelSearchPermission = getRolesForPath("/search").some((role) =>
    userRoles.includes(role),
  );
  useTravelSearchShortcut(hasTravelSearchPermission);
  const {
    username,
    userImageUrl,
    menuItems,
    isMenuOpen,
    handleMenuToggle,
    handleMenuItemClick,
  } = useUserMenuData();

  return (
    <div className={layoutStyles.appContainer}>
      {/* Mobile Backdrop */}
      <div
        className={`${layoutStyles.backdrop} ${isMobileSidebarOpen ? layoutStyles.backdropVisible : ""}`}
        onClick={() => handleMobileSidebarChange(false)}
        aria-hidden="true"
      />

      <div className={layoutStyles.appBody}>
        <Sidebar
          mobileOpen={isMobileSidebarOpen}
          setMobileOpen={handleMobileSidebarChange}
        />

        <div
          className={layoutStyles.workspaceColumn}
          inert={isMobile && isMobileSidebarOpen ? "" : undefined}
        >
          <header className={layoutStyles.appHeader}>
            <div className={layoutStyles.headerStart}>
              <button
                ref={menuButtonRef}
                type="button"
                className={layoutStyles.menuButton}
                onClick={() => setMobileSidebarOpen(true)}
                aria-label={t("common.openMenu", "Open menu")}
                aria-expanded={isMobileSidebarOpen}
                aria-controls="app-sidebar"
              >
                <MenuSvg />
              </button>
              <span className={layoutStyles.headerBrand}>AVIAFY</span>
              <Link
                to={getDefaultRouteForRoles(userRoles)}
                className={layoutStyles.mobileIdentity}
                aria-label={t("nav.home", "Go to home")}
                title={t("nav.home", "Go to home")}
              >
                {/* Mobile Logo */}
                <div
                  className={`${sidebarStyles.companyLogoWrapper} ${layoutStyles.mobileCompanyLogo}`}
                >
                  {!logoUrl || logoError ? (
                    <div className={sidebarStyles.companyLogoPlaceholder}>
                      {(company?.name || "A").charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <>
                      <div
                        className={`${sidebarStyles.companyLogoPlaceholder} ${sidebarStyles.companyLogoPlaceholderOverlay} ${logoLoaded ? sidebarStyles.companyLogoPlaceholderHidden : ""}`}
                        aria-hidden="true"
                      >
                        {(company?.name || "A").charAt(0).toUpperCase()}
                      </div>
                      <img
                        src={logoUrl}
                        alt={
                          company?.name ||
                          t("companyProfile.company", "Company")
                        }
                        className={`${sidebarStyles.companyLogo} ${logoLoaded ? sidebarStyles.companyLogoLoaded : ""}`}
                        decoding="async"
                        crossOrigin="anonymous"
                        onLoad={handleLoad}
                        onError={handleError}
                      />
                    </>
                  )}
                </div>
                <span
                  className={layoutStyles.mobileLogo}
                  title={company?.name || "AVIAFY"}
                >
                  {company?.name || "AVIAFY"}
                </span>
              </Link>
            </div>
            <div
              className={layoutStyles.headerActions}
              aria-label={t("topBar.actions", "Top actions")}
            >
              {hasAgentPermission && (
                <NavLink
                  to="/agent"
                  className={({ isActive }) =>
                    `${layoutStyles.agentButton} ${isActive ? layoutStyles.active : ""}`
                  }
                  aria-label={t("menu.agent", "AI assistant")}
                  title={t("menu.agent", "AI assistant")}
                >
                  <HiOutlineSparkles
                    size={20}
                    aria-hidden="true"
                    focusable="false"
                  />
                  <span className={layoutStyles.agentLabel}>
                    {t("menu.agent", "AI assistant")}
                  </span>
                  <span
                    className={layoutStyles.agentShortLabel}
                    aria-hidden="true"
                  >
                    {t("topBar.aiShort", "AI")}
                  </span>
                </NavLink>
              )}
              <NotificationIcon
                icon={HiOutlineChatBubbleLeftRight}
                typeFilter="messages"
                ariaLabel={t("topBar.messages", "Messages")}
              />
              <NotificationIcon
                typeFilter="alerts"
                ariaLabel={t("topBar.notifications", "Notifications")}
              />
              <UserMenu
                username={username}
                userImageUrl={userImageUrl}
                isOpen={isMenuOpen}
                onToggle={handleMenuToggle}
                dropdownData={menuItems}
                onItemClick={handleMenuItemClick}
              />
            </div>
          </header>

          <main
            className={layoutStyles.mainContent}
            aria-label={t("common.mainContent", "Main content")}
          >
            {showSubscriptionBanner && (
              <div
                role="alert"
                style={{
                  background: "#f59e0b",
                  color: "#000",
                  padding: "0.75rem 1rem",
                  textAlign: "center",
                }}
              >
                {t(
                  "billing.subscriptionInactive",
                  "Your subscription is inactive.",
                )}{" "}
                {canManageBilling ? (
                  <Link
                    to="/settings/billing"
                    style={{ color: "#000", textDecoration: "underline" }}
                  >
                    {t(
                      "billing.renewToRestoreAccess",
                      "Renew to restore access.",
                    )}
                  </Link>
                ) : (
                  t(
                    "billing.contactOwnerToRestoreAccess",
                    "Contact your company owner to restore access.",
                  )
                )}
              </div>
            )}
            <SettingsLayout>{children}</SettingsLayout>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
